import React from 'react'

interface FieldProps {
  id: string
  label: string
  hint?: string
  error?: string
  children: React.ReactNode
}

// describedBy is the ids an input's hint and error carry.
export const hintId = (id: string) => `${id}-hint`
export const errorId = (id: string) => `${id}-error`

// Field labels one input and shows its hint, or its error once there is one.
// The input (the child with this id) is told about both: aria-describedby
// points at the hint or error, and aria-invalid is set while there's an error,
// so focusFirstInvalid can find it after a failed save.
export default function Field({ id, label, hint, error, children }: FieldProps) {
  const described = error ? errorId(id) : hint ? hintId(id) : undefined
  const content = React.Children.map(children, (child) => {
    if (!React.isValidElement<{ id?: string; 'aria-describedby'?: string; 'aria-invalid'?: unknown }>(child) || child.props.id !== id) {
      return child
    }
    const own = child.props['aria-describedby']
    return React.cloneElement(child, {
      'aria-describedby': [own, described].filter(Boolean).join(' ') || undefined,
      'aria-invalid': error ? true : child.props['aria-invalid'],
    })
  })
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {content}
      {error ? (
        <p id={errorId(id)} className="field-error" role="alert">{error}</p>
      ) : (
        hint && <p id={hintId(id)} className="field-hint">{hint}</p>
      )}
    </div>
  )
}

// focusFirstInvalid moves focus to the first field marked invalid inside
// root, after a failed save (review 2026-10-14). Call it once the errors have
// rendered (useFocusFirstInvalid does).
export function focusFirstInvalid(root: ParentNode | null): void {
  const target = root?.querySelector<HTMLElement>('[aria-invalid="true"]')
  target?.focus()
}

// useFocusFirstInvalid focuses the first invalid field whenever a new set of
// errors appears.
export function useFocusFirstInvalid(errors: Record<string, string> | null | undefined, root: React.RefObject<HTMLElement>): void {
  React.useEffect(() => {
    if (errors && Object.keys(errors).length > 0) focusFirstInvalid(root.current)
  }, [errors, root])
}
